import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@microbeta/ui";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCheck,
  MdNotifications,
  MdNotificationsNone,
  MdPersonAdd,
  MdThumbUp,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  useAcceptFriendRequest,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useRejectFriendRequest,
} from "../../../../features/social/api/mutations";
import {
  useNotifications,
  usePendingRequests,
  useUnreadNotificationCount,
} from "../../../../features/social/api/queries";
import type { NotificationItem } from "../../../../features/social/api/social-api";
import { paths } from "../../../../shared/utils/routing";
import styles from "./notification-dropdown.module.scss";

/** Returns a relative time string */
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getNotificationIcon(type: NotificationItem["type"]) {
  switch (type) {
    case "friend_request":
      return <MdPersonAdd />;
    case "friend_accepted":
      return <MdCheck />;
    case "post_like":
      return <MdThumbUp />;
    case "post_comment":
    case "post_mention":
      return <MdThumbUp />;
    default:
      return <MdNotificationsNone />;
  }
}

function getNotificationText(
  type: NotificationItem["type"],
  actorName: string,
  t: (key: string, fallback: string) => string
): string {
  switch (type) {
    case "friend_request":
      return t(
        "social.notifications.friendRequest",
        "{{name}} sent you a friend request"
      ).replace("{{name}}", actorName);
    case "friend_accepted":
      return t(
        "social.notifications.friendAccepted",
        "{{name}} accepted your friend request"
      ).replace("{{name}}", actorName);
    case "post_like":
      return t(
        "social.notifications.postLike",
        "{{name}} stoked your post"
      ).replace("{{name}}", actorName);
    case "post_comment":
      return t(
        "social.notifications.postComment",
        "{{name}} commented on your post"
      ).replace("{{name}}", actorName);
    case "post_mention":
      return t(
        "social.notifications.postMention",
        "{{name}} mentioned you in a post"
      ).replace("{{name}}", actorName);
    default:
      return actorName;
  }
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export interface NotificationDropdownProps {
  className?: string;
  isMobileButton?: boolean;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  className,
  isMobileButton,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Use the queries only when the dropdown is open (or always if we want the badge to update)
  // Badge uses useUnreadNotificationCount separately to be lightweight.
  const { data: unreadData } = useUnreadNotificationCount();
  const unreadCount = unreadData?.count ?? 0;

  const { data, isLoading } = useNotifications();
  const { data: pendingData } = usePendingRequests();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const acceptRequest = useAcceptFriendRequest();
  const rejectRequest = useRejectFriendRequest();

  const pendingUserIds = new Set(
    pendingData?.pendingRequests?.map((r) => r.id) ?? []
  );

  const notifications = data?.notifications || [];
  const hasUnread = notifications.some((n) => !n.readAt);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {isMobileButton ? (
          <Button
            variant="ghost"
            className={`${className || ""}`}
            style={{
              width: "100%",
              justifyContent: "flex-start",
              padding: "0.75rem 1.5rem",
              gap: "1rem",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "1.25rem",
              }}
            >
              <MdNotifications />
            </span>
            <Text variant="medium" style={{ flex: 1, textAlign: "left" }}>
              {t("navigation.notifications", "Notifications")}
            </Text>
            {unreadCount > 0 && (
              <span className={styles.badge} style={{ position: "static" }}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
        ) : (
          <button
            type="button"
            className={`${styles.bellButton} ${unreadCount > 0 ? styles.hasUnread : ""} ${className || ""}`}
          >
            <MdNotifications className={styles.bellIcon} />
            {unreadCount > 0 && (
              <span className={styles.badge}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        className={styles.dropdownContent}
        align="end"
        sideOffset={8}
      >
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            {t("social.notifications.title", "Notifications")}
          </span>
          {hasUnread && notifications.length > 0 && (
            <button
              type="button"
              className={styles.markAllReadBtn}
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              {t("social.notifications.markAllRead", "Mark all read")}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.emptyState}>
            <span>{t("common.loading", "Loading...")}</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.emptyState}>
            <MdNotificationsNone className={styles.emptyIcon} />
            <span>
              {t("social.notifications.emptyTitle", "No notifications yet")}
            </span>
          </div>
        ) : (
          <div className={styles.notificationList}>
            {notifications.map((notification) => {
              const showFriendActions =
                notification.type === "friend_request" &&
                pendingUserIds.has(notification.actor.id);

              return (
                <button
                  key={notification.id}
                  type="button"
                  className={`${styles.notificationItem} ${
                    !notification.readAt ? styles.unread : ""
                  }`}
                  onClick={() => {
                    if (!notification.readAt) {
                      markRead.mutate(notification.id);
                    }
                    if (
                      (notification.type === "friend_request" ||
                        notification.type === "friend_accepted") &&
                      notification.actor.handle
                    ) {
                      setIsOpen(false);
                      navigate(paths.publicProfile(notification.actor.handle));
                    }
                  }}
                >
                  {notification.actor.avatarUrl ? (
                    <img
                      src={notification.actor.avatarUrl}
                      alt={notification.actor.displayName}
                      className={styles.avatar}
                    />
                  ) : (
                    <div className={styles.avatarFallback}>
                      {getInitials(notification.actor.displayName)}
                    </div>
                  )}

                  <div className={styles.content}>
                    <div className={styles.textRow}>
                      <span className={styles.icon}>
                        {getNotificationIcon(notification.type)}
                      </span>
                      <span className={styles.text}>
                        {getNotificationText(
                          notification.type,
                          notification.actor.displayName,
                          t
                        )}
                      </span>
                      <span className={styles.time}>
                        {timeAgo(notification.createdAt)}
                      </span>
                    </div>

                    {showFriendActions && (
                      <div className={styles.actions}>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            acceptRequest.mutate(notification.actor.id);
                            if (!notification.readAt) {
                              markRead.mutate(notification.id);
                            }
                          }}
                          disabled={acceptRequest.isPending}
                        >
                          {t("social.friends.accept", "Accept")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            rejectRequest.mutate(notification.actor.id);
                            if (!notification.readAt) {
                              markRead.mutate(notification.id);
                            }
                          }}
                          disabled={rejectRequest.isPending}
                        >
                          {t("social.friends.reject", "Decline")}
                        </Button>
                      </div>
                    )}
                  </div>
                  {!notification.readAt && (
                    <span className={styles.unreadIndicator} />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {notifications.length > 0 && (
          <div
            style={{
              padding: "var(--spacing-sm)",
              borderTop: "1px solid var(--color-border)",
              textAlign: "center",
            }}
          >
            <Button
              variant="ghost"
              size="small"
              onClick={() => {
                setIsOpen(false);
                navigate(paths.social.notifications);
              }}
              style={{ width: "100%" }}
            >
              {t("social.notifications.viewAll", "View all notifications")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
